import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Download, Search, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { documentoPublicoService } from '../../api/services';
import { Loading, Vazio, Badge, Input, Select, Paginacao } from '../../components/ui';
import { CATEGORIAS_DOCUMENTO } from '../../types';
import type { Documento } from '../../types';
import { formatarTamanho, formatarData, labelCategoria } from '../../utils/formatters';
import './transparencia.css';

const LIMITE = 12;

export default function TransparenciaPage() {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [filtrados, setFiltrados] = useState<Documento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    documentoPublicoService.listar()
      .then(r => { setDocumentos(r.documentos); setFiltrados(r.documentos); })
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    let lista = documentos;
    if (categoria) lista = lista.filter(d => d.categoria === categoria);
    if (busca) {
      const q = busca.toLowerCase();
      lista = lista.filter(d =>
        d.titulo.toLowerCase().includes(q) || d.nota?.toLowerCase().includes(q)
      );
    }
    setFiltrados(lista);
    setPagina(1);
  }, [busca, categoria, documentos]);

  const totalPaginas = Math.ceil(filtrados.length / LIMITE);
  const inicio = (pagina - 1) * LIMITE;
  const paginados = filtrados.slice(inicio, inicio + LIMITE);

  return (
    <div className="transparencia-page">
      <section className="page-header">
        <div className="container">
          <div className="transparencia-header-row">
            <div className="page-header-conteudo">
              <div className="secao-tag">Portal de Transparência</div>
              <h1>Documentos Públicos</h1>
              <p>Atas, balancetes, certidões, estatuto e demais documentos oficiais da LICMAB.</p>
            </div>
            <Link to="/admin/login" className="btn-admin-acesso" title="Área administrativa">
              <Lock size={15} />
              <span>Área Admin</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="transparencia-conteudo">
        <div className="container">
          {/* Filtros */}
          <div className="filtros-barra">
            <div className="filtros-esquerda">
              <Input
                placeholder="Buscar documentos..."
                value={busca}
                onChange={e => setBusca(e.target.value)}
                icone={<Search size={16} />}
              />
              <Select
                opcoes={CATEGORIAS_DOCUMENTO}
                value={categoria}
                onChange={e => setCategoria(e.target.value)}
                placeholder="Todas as categorias"
              />
            </div>
            <div className="filtros-total">
              {filtrados.length} documento{filtrados.length !== 1 ? 's' : ''} encontrado{filtrados.length !== 1 ? 's' : ''}
            </div>
          </div>

          {carregando ? (
            <Loading mensagem="Carregando documentos..." />
          ) : paginados.length === 0 ? (
            <Vazio
              icone={<FileText size={48} />}
              titulo="Nenhum documento encontrado"
              descricao="Tente ajustar os filtros de busca."
            />
          ) : (
            <>
              <div className="docs-lista">
                {paginados.map(doc => (
                  <DocumentoCard key={doc.id} doc={doc} />
                ))}
              </div>
              <Paginacao
                paginaAtual={pagina}
                totalPaginas={totalPaginas}
                onMudar={setPagina}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function DocumentoCard({ doc }: { doc: Documento }) {
  const handleDownload = () => {
    const url = documentoPublicoService.downloadUrl(doc.id);
    window.open(url, '_blank');
  };

  return (
    <div className="doc-card">
      <div className="doc-icone">
        <FileText size={24} />
      </div>
      <div className="doc-info">
        <div className="doc-header-info">
          <Badge texto={labelCategoria(doc.categoria)} cor="azul" />
          {doc.data && (
            <span className="doc-data">{formatarData(doc.data)}</span>
          )}
        </div>
        <h3 className="doc-titulo">{doc.titulo}</h3>
        {doc.nota && <p className="doc-nota">{doc.nota}</p>}
        <div className="doc-meta">
          <span>{doc.tipoArquivo?.toUpperCase() || 'PDF'}</span>
          {doc.tamanhoArquivo > 0 && <span>{formatarTamanho(doc.tamanhoArquivo)}</span>}
          <span>Publicado em {formatarData(doc.criadoEm)}</span>
        </div>
      </div>
      {doc.urlPublica && (
        <button className="doc-download" onClick={handleDownload} title="Baixar documento">
          <Download size={18} />
          <span>Baixar</span>
        </button>
      )}
    </div>
  );
}